type BaseSchema = {
  "@context": string;
  "@graph": [
    OrganizationGraph,
    WebSiteGraph,
    WebPageGraph,
    BlogGraph?,
  ];
};

type BaseGraph = {
  "@type": string;
  "@id": string;
  name: string;
  url: string;
};

type OrganizationGraph = BaseGraph & {
  logo: { "@type": string; url: string };
  sameAs?: string[];
  contactPoint?: {
    "@type": string;
    telephone: string;
    contactType: string;
  };
};

type WebPageGraph = BaseGraph & {
  isPartOf: { "@id": string };
  description?: string;
  about?: { "@id": string };
  mainEntity?: { "@id": string };
};

type WebSiteGraph = BaseGraph & {
  publisher?: { "@id": string };
};

type BlogGraph = {
  "@type": string;
  "@id": string;
  headline: string;
  description: string;
  articleSection: string;
  url: string;
  mainEntityOfPage: { "@id": string };
  datePublished: string;
  dateModified: string;
  image: { "@type": string; url: string; width: number; height: number };
  author: {
    "@type": string;
    name: string;
    description: string;
    image?: { "@type": string; url: string };
  };
  publisher: { "@id": string };
  isPartOf: { "@id": string };
};

type BlogArguments = {
  section: string;
  datePublished: string;
  dateModified: string;
  image: string;
  socialImage: string;
  author?: {
    name: string;
    job_title: string;
    image: string;
  };
};

const getSchema = (url: string, name: string, description: string, blog?: BlogArguments): BaseSchema => {
  const organizationGraph : OrganizationGraph =
  {
    "@type": "Organization",
    "@id": "https://upsun.com/#organization",
    "name": "Upsun",
    "url": "https://upsun.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://upsun.com/logos/logo_horizontal_black.svg"
    }
  };

  const webPageGraph : WebPageGraph =
  {
    "@type": "WebPage",
    "@id": `https://upsun.com${url}#webpage`,
    "url": `https://upsun.com${url}`,
    "name": name,
    "isPartOf": { "@id": "https://upsun.com/#website" }
  }

  if(blog) {
    webPageGraph.mainEntity = { "@id": `https://upsun.com${url}#webpage` };
  } else {
    webPageGraph.description = description;
    webPageGraph.about = { "@id": "https://upsun.com/#organization" };

    organizationGraph.sameAs = [
      "https://x.com/upsuncom",
      "https://www.reddit.com/r/upsun/",
      "https://bsky.app/profile/upsun.com",
      "https://www.youtube.com/@upsundotcom",
      "https://github.com/upsun",
      "https://x.com/upsundotcom",
      "https://www.linkedin.com/company/upsundotcom"
    ];

    organizationGraph.contactPoint = {
      "@type": "ContactPoint",
      "telephone": "+1 818-369-4597",
      "contactType": "Customer Service"
    };
  }

  const schema : BaseSchema = {
    "@context": "https://schema.org",
    "@graph": [
      organizationGraph,
      {
        "@type": "WebSite",
        "@id": "https://upsun.com/#website",
        "url": "https://upsun.com",
        "name": "Upsun",
        "publisher": { "@id": "https://upsun.com/#organization" }
      },
      webPageGraph,
    ]
  }

  const blogGraph : BlogGraph = {
    "@type": "BlogPosting",
    "@id": `https://upsun.com${url}#article`,
    "headline": name,
    "description": description,
    "articleSection": blog?.section || "",
    "url": `https://upsun.com${url}`,
    "mainEntityOfPage": { "@id": `https://upsun.com${url}#webpage` },
    "datePublished": blog?.datePublished || "",
    "dateModified": blog?.dateModified || "",
    "image": {
      "@type": "ImageObject",
      "url": blog?.socialImage ? `https://upsun.com${blog?.socialImage}` : "",
      "width": 1200,
      "height": 630
    },
    "author": {
      "@type": "Organization",
      "name": "Upsun",
      "description": "Cloud application platform"
    },
    "publisher": { "@id": "https://upsun.com/#organization" },
    "isPartOf": { "@id": "https://upsun.com/#website" }
  }

  if(blog?.author) {
    blogGraph.author = {
      "@type": "Person",
      "name": blog?.author.name || "",
      "description": blog?.author.job_title || "",
      "image": {
        "@type": "ImageObject",
        "url": blog?.author.image ? `https://upsun.com${blog?.author.image}` : ""
      }
    }
  }

  if(blog) {
    schema["@graph"].push(blogGraph);
  }

  return schema;
}

export { getSchema, BlogArguments };